import React, { useEffect, useState, useContext, useRef } from "react";
import { AuthContext } from "../../routes/AuthContext";
import { toast } from "react-toastify";
import "./Address.css";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ADDRESS_TYPE_MAP = {
  Home: 1,
  Office: 2,
  Work: 3,
  PG: 4,
  Other: 5,
};

const Address = () => {
  // ─── Get logged-in customer info from context
  const { getCustomerData, isAuthenticated } = useContext(AuthContext);
  const customer = getCustomerData();
  const customer_id = customer?.customer_id;
  const customerName = customer?.name || "";

  // ─── Empty form template — reused when resetting
  const emptyForm = {
    name: "",
    customer_address_type: "Home",
    door_number: "",
    building_street: "",
    area: "",
    landmark: "",
    city: "",
    state: "",
    country: "india",
    pincode: "",
    contact_mobile_number: "",
  };

  // ─── State
  const [addresses, setAddresses] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editAddressId, setEditAddressId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null); // which card's menu is open
  const [loading, setLoading] = useState(false);

  // ─── Ref: close dropdown when clicking outside
  const menuRef = useRef(null);

  useEffect(() => {
    const closeMenu = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", closeMenu);
    return () => document.removeEventListener("mousedown", closeMenu);
  }, []);

  // ─────────────────────────────────
  //  FETCH all addresses
  // ─────────────────────────────────
  const fetchAddresses = async (cid) => {
    if (!cid) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("customer_id", cid);

      const res = await fetch(
        `${BASE_URL}customer_address_list`,
        { method: "POST", body: formData },
      );
      const data = await res.json();

      if (data.api_success && data.data) {
        const raw = data.data[""] || [];

        // flatten deeply & filter only valid objects
        const normalized = raw
          .flat(Infinity)
          .filter((item) => item && item.customer_address_id);

        setAddresses(normalized);
      } else {
        setAddresses([]);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load addresses");
      setAddresses([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch when customer_id is ready
  useEffect(() => {
    if (customer_id) fetchAddresses(customer_id);
  }, [customer_id]);

  // ─────────────────────────────────
  //  INPUT CHANGE
  // ─────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // ─────────────────────────────────
  //  SAVE (Insert or Update)
  // ─────────────────────────────────
  const handleSave = async () => {
    if (!customer_id) {
      toast.warning("Please login");
      return;
    }

    const required = [
      "name",
      "door_number",
      "building_street",
      "area",
      "city",
      "state",
      "pincode",
      "contact_mobile_number",
    ];
    for (let field of required) {
      if (!form[field]?.trim()) {
        toast.warning(`Please fill in ${field.replace(/_/g, " ")}`);
        return;
      }
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("customer_id", customer_id);
      formData.append(
        "customer_address_type",
        ADDRESS_TYPE_MAP[form.customer_address_type] || 1,
      );

      // ✅ NEW FIELD (backend added this)
      formData.append("name", form.name);

      formData.append("door_number", form.door_number);
      formData.append("building_street", form.building_street);
      formData.append("area", form.area);
      formData.append("landmark", form.landmark || "");
      formData.append("city", form.city);
      formData.append("state", form.state);
      formData.append("country", form.country);
      formData.append("pincode", form.pincode);
      formData.append("contact_mobile_number", form.contact_mobile_number);

      let url = `${BASE_URL}customer_address_insert`;
      if (editMode && editAddressId !== null) {
        url = `${BASE_URL}customer_address_update`;
        formData.append("customer_address_id", editAddressId);
      }

      const res = await fetch(url, { method: "POST", body: formData });
      const data = await res.json();

      if (data.api_success) {
        toast.success(editMode ? "Address updated ✅" : "Address added ✅");
        resetForm();
        await fetchAddresses(customer_id);
      } else {
        toast.error(data.message || "Failed to save");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to save address");
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────
  //  DELETE
  // ─────────────────────────────────
  const handleDelete = async (addressId) => {
    if (!window.confirm("Delete this address?")) return;
    setOpenMenuId(null);
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("customer_id", customer_id);
      formData.append("customer_address_id", addressId);

     const res = await fetch(`${BASE_URL}customer_address_remove`,{ method: "POST", body: formData },);
      const data = await res.json();

      if (data.api_success) {
        toast.success("Address deleted ✅");
        setAddresses((prev) =>
          prev.filter(
            (a) => Number(a.customer_address_id) !== Number(addressId),
          ),
        );
      } else {
        toast.error(data.message || "Failed to delete");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete");
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────
  //  EDIT — open form pre-filled
  // ─────────────────────────────────
  const handleEdit = (addr) => {
    setForm({
      name: addr.name || "",
      customer_address_type: addr.customer_address_type_name || "Home",
      door_number: addr.door_number || "",
      building_street: addr.building_street || "",
      area: addr.area || "",
      landmark: addr.landmark || "",
      city: addr.city || "",
      state: addr.state || "",
      country: addr.country || "india",
      pincode: addr.pincode || "",
      contact_mobile_number: addr.contact_mobile_number || "",
    });
    setEditMode(true);
    setEditAddressId(addr.customer_address_id);
    setShowForm(true);
    setOpenMenuId(null);
  };

  // ─────────────────────────────────
  //  RESET form
  // ─────────────────────────────────
  const resetForm = () => {
    setShowForm(false);
    setEditMode(false);
    setEditAddressId(null);
    setForm(emptyForm);
  };

  if (!isAuthenticated) {
    return (
      <p className="text-center text-muted p-5">
        Please login to manage addresses
      </p>
    );
  }

  // ─────────────────────────────────
  //  RENDER
  // ─────────────────────────────────
  return (
    <div className="address-page">
      {/* Title */}
      <h5 className="fw-bold mb-3">Manage Addresses</h5>

      {/* "+ ADD A NEW ADDRESS" — only when form is hidden */}
      {!showForm && (
        <div className="address-box mb-3">
          <button
            className="location-btn"
            onClick={() => {
              setShowForm(true);
              setEditMode(false);
              setForm(emptyForm);
            }}
          >
            + ADD A NEW ADDRESS
          </button>
        </div>
      )}

      {/* ── Add / Edit Form ── */}
      {showForm && (
        <div className="address-box mb-3">
          <h6 className="fw-bold mb-3">
            {editMode ? "Edit Address" : "New Address"}
          </h6>
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleInputChange}
            placeholder="Full Name *"
            className="form-control mb-3"
          />

          <select
            name="customer_address_type"
            value={form.customer_address_type}
            onChange={handleInputChange}
            className="form-select mb-3"
          >
            <option value="">Select Address Type</option>
            <option value="Home">Home</option>
            <option value="Office">Office</option>
            <option value="Work">Work</option>
            <option value="PG">PG</option>
            <option value="Other">Other</option>
          </select>

          <div className="row g-3 mb-3">
            <div className="col-6">
              <input
                type="text"
                name="door_number"
                value={form.door_number}
                onChange={handleInputChange}
                placeholder="Door Number *"
                className="form-control"
              />
            </div>
            <div className="col-6">
              <input
                type="text"
                name="building_street"
                value={form.building_street}
                onChange={handleInputChange}
                placeholder="Building / Street *"
                className="form-control"
              />
            </div>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-6">
              <input
                type="text"
                name="area"
                value={form.area}
                onChange={handleInputChange}
                placeholder="Area *"
                className="form-control"
              />
            </div>
            <div className="col-6">
              <input
                type="text"
                name="landmark"
                value={form.landmark}
                onChange={handleInputChange}
                placeholder="Landmark"
                className="form-control"
              />
            </div>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-6">
              <input
                type="text"
                name="city"
                value={form.city}
                onChange={handleInputChange}
                placeholder="City *"
                className="form-control"
              />
            </div>
            <div className="col-6">
              <input
                type="text"
                name="state"
                value={form.state}
                onChange={handleInputChange}
                placeholder="State *"
                className="form-control"
              />
            </div>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-6">
              <input
                type="text"
                name="pincode"
                value={form.pincode}
                onChange={handleInputChange}
                placeholder="Pincode *"
                maxLength="6"
                className="form-control"
              />
            </div>
            <div className="col-6">
              <input
                type="text"
                name="contact_mobile_number"
                value={form.contact_mobile_number}
                onChange={handleInputChange}
                placeholder="Mobile Number *"
                maxLength="10"
                className="form-control"
              />
            </div>
          </div>

          <div className="d-flex gap-3 mt-4">
            <button
              className="save-btn"
              onClick={handleSave}
              disabled={loading}
            >
              {loading
                ? "Saving..."
                : editMode
                  ? "Update Address"
                  : "Save Address"}
            </button>
            <button
              className="cancel-btn"
              onClick={resetForm}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && <p className="text-center text-muted">Loading...</p>}

      {/* Empty */}
      {!loading && addresses.length === 0 && !showForm && (
        <p className="text-center text-muted mt-4">No addresses saved yet.</p>
      )}

      {/* ── Saved Address Cards ── */}
  {/* ── Address List ── */}
{addresses.map((addr) => (
  <div key={addr.customer_address_id} className="address-card">

    {/* Top Row: Address Type + Three Dots */}
    <div className="address-top-row">
      <span className="address-type-badge">
        {(addr.customer_address_type_name || "ADDRESS").toUpperCase()}
      </span>

      {/* ⋮ menu wrapper — UNCHANGED */}
      <div
        className="menu-wrapper"
        ref={
          Number(openMenuId) === Number(addr.customer_address_id)
            ? menuRef
            : null
        }
      >
        <button
          className="dots-btn"
          onClick={() =>
            setOpenMenuId(
              Number(openMenuId) === Number(addr.customer_address_id)
                ? null
                : addr.customer_address_id
            )
          }
        >
          ⋮
        </button>

        {Number(openMenuId) === Number(addr.customer_address_id) && (
          <div className="dropdown-menu-box">
            <button
              className="dropdown-item-btn"
              onClick={() => handleEdit(addr)}
            >
              Edit
            </button>
            <button
              className="dropdown-item-btn"
              onClick={() =>
                handleDelete(addr.customer_address_id)
              }
            >
              Delete
            </button>
          </div>
        )}
      </div>
    </div>

    {/* Name + Mobile */}
    <div className="address-header">
      <span className="address-name">{addr.name}</span>
      <span className="address-mobile">
        {addr.contact_mobile_number}
      </span>
    </div>

    {/* Full Address */}
    <p className="address-line">
      {[
        addr.building_street,
        addr.door_number,
        addr.area,
        addr.city,
        addr.state,
      ]
        .filter(Boolean)
        .join(", ")}
      {addr.pincode ? ` - ${addr.pincode}` : ""}
    </p>

  </div>
))}


    </div>
  );
};

export default Address;
